<template>
  <div class="flex flex-wrap space-x-1">
    <deal-status-dropdown
      name="status_dorpdown"
      :options="dealStatusOptions"
      :value="activeDeal?.workflow_snapshot?.workflow_status?.status"
      :color="color"
      :teleport-options="{ enabled: true }"
      :is-loading="isDealStatusLoading"
      @update:status="updateDealStatus"
    />
    <deal-status-dropdown
      v-if="
        Object.keys(dealSubStatusOptions).length ||
        activeDeal?.workflow_snapshot?.workflow_status?.sub_status
      "
      name="sub_status_dorpdown"
      :options="dealSubStatusOptions"
      :value="activeDeal?.workflow_snapshot?.workflow_status?.sub_status"
      :color="color"
      :teleport-options="{ enabled: true }"
      :disabled="!Object.keys(dealSubStatusOptions).length"
      :is-loading="isDealSubStatusUpdating"
      @update:status="updateDealSubStatus"
    />
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useStore } from "vuex";

import isEmpty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";

import {
  DEFAULT_WORKFLOW_STATUS_COLOR,
  STATUS_DEAD,
} from "@/helpers/constants";
import { hexToRgb } from "@/helpers/formatting";
import { usePromiseWrapper } from "@/hooks/common";
import { useDeals } from "@/hooks/deals";

import DealStatusDropdown from "@/components/deals/DealStatusDropdown.vue";

import type { ApplicationStatusPayload } from "@/models/applications";
import type {
  IWorkflowStatus,
  IWorkflowSubStatusWeight,
} from "@/models/workflows";

const { dispatch, commit, getters } = useStore();
const { activeDeal } = useDeals();

const isLive = computed(() => activeDeal.value?.status !== STATUS_DEAD);

const statuses = computed<IWorkflowStatus[]>(() =>
  getters["applications/workflowStatusesByDealId"](activeDeal.value?.id)
);

const statusesByType = computed(() => {
  const options =
    statuses.value?.filter(({ live }) => live === isLive.value) ?? [];
  return orderBy(options, "weight");
});

const color = computed(() => {
  const statusColor = String(
    activeDeal.value?.workflow_snapshot?.workflow_status?.color
  ).toLocaleLowerCase();

  return hexToRgb(statusColor) ? statusColor : DEFAULT_WORKFLOW_STATUS_COLOR;
});

const dealStatusOptions = computed(() => {
  return (
    statusesByType.value.reduce(
      (acc: Record<string, string>, status: IWorkflowStatus) => {
        acc[status.name] = status.name;
        return acc;
      },
      {}
    ) || {}
  );
});

const dealSubStatusOptions = computed(() => {
  const dealStatus = statusesByType.value.find(
    (status) =>
      status.name ===
      activeDeal.value?.workflow_snapshot?.workflow_status?.status
  );
  const sortedSubStatus: IWorkflowSubStatusWeight[] = orderBy(
    dealStatus?.substatuses_weight ?? [],
    "weight"
  );
  return sortedSubStatus.reduce(
    (acc: Record<string, string>, status: IWorkflowSubStatusWeight) => {
      acc[status.name] = status.name;
      return acc;
    },
    {}
  );
});

const updateApplicationInList = (value: ApplicationStatusPayload) => {
  commit("applications/updateApplicationDataInList", {
    application_id: activeDeal.value?.id,
    path: "workflow_status",
    value,
  });
};

const colorByStatusName = (name: ApplicationStatusPayload["status"]) => {
  return (
    statuses.value.find((status: IWorkflowStatus) => status.name === name)
      ?.color ?? DEFAULT_WORKFLOW_STATUS_COLOR
  );
};

const { fetchWrapper: updateDealStatus, loading: isDealStatusLoading } =
  usePromiseWrapper(async (status: string | number) => {
    const payload = {
      status: String(status),
      sub_status: null,
      color: colorByStatusName(String(status)),
    };
    await dispatch("applications/updateWorkflowStatus", {
      id: activeDeal.value?.id,
      payload,
    });
    updateApplicationInList(payload);
  });

const { fetchWrapper: updateDealSubStatus, loading: isDealSubStatusUpdating } =
  usePromiseWrapper(async (subStatus: string | number) => {
    const payload = {
      status: activeDeal.value?.workflow_snapshot?.workflow_status?.status,
      sub_status: String(subStatus),
      color: activeDeal.value?.workflow_snapshot?.workflow_status?.color,
    };
    await dispatch("applications/updateWorkflowStatus", {
      id: activeDeal.value?.id,
      payload,
    });
    updateApplicationInList(payload);
  });

const { fetchWrapper: getStatuses } = usePromiseWrapper(async () => {
  if (isEmpty(statuses.value)) {
    await dispatch("applications/getWorkflowStatuses", activeDeal.value?.id);
  }
});

onMounted(() => {
  getStatuses();
});
</script>
